import React from 'react'
import { Link } from '@tanstack/react-router'
import { useTar } from '@/modules/i18n/useTar'
import { Logo } from '../Misc/Logo'
import { ExternalURLs } from '@/types/externalURLs'
import { LinkedInIcon, DiscordIcon } from '@duck/ui'
import { useLocalizedHref } from '@/hooks/useLocalizedHref'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { useUserVoice } from '@/hooks/useUserVoice'

export function Footer() {
    const { t } = useTar()
    const { openVoice } = useUserVoice()
    const currentYear = new Date().getFullYear()
    const links = {
        home: useLocalizedHref('/'),
        about: useLocalizedHref('/about'),
        faq: useLocalizedHref('/faq'),
        takeHome: useLocalizedHref('/take-home-assignments'),
        feedback: useLocalizedHref('/xp-lab-feedback'),
        ai: useLocalizedHref('/using-ai-without-outsourcing-judgment'),
        analytical: useLocalizedHref('/analytical-workflow'),
        privacy: useLocalizedHref('/privacy-policy'),
        terms: useLocalizedHref('/terms-of-service'),
        employers: useLocalizedHref('/for-employers'),
        education: useLocalizedHref('/for-education'),
    }

    return (
        <footer className="w-full border-t border-border bg-card/50 backdrop-blur-sm mt-auto">
            <div className="max-w-6xl mx-auto px-8 py-8">
                <div className="grid grid-cols-1 gap-8 text-center md:grid-cols-2 md:text-start xl:grid-cols-5">
                    {/* Brand Section */}
                    <div className="space-y-4 flex flex-col items-center md:col-span-2 md:items-start xl:col-span-1">
                        <Link to={links.home} className="flex items-center gap-2 w-fit">
                            <Logo className="text-2xl" />
                        </Link>
                    </div>

                    {/* Challenges Section */}
                    <div className="space-y-4 flex flex-col items-center md:items-start">
                        <h3 className="font-semibold text-foreground">{t('navigation.challenges')}</h3>
                        <ul className="space-y-3 flex flex-col items-center md:items-start">
                            <li>
                                <Link
                                    to="/challenges/available"
                                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                                >
                                    {t('challenges.availableChallenges')}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/challenges/completed"
                                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                                >
                                    {t('navigation.completedChallenges')}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Guides Section */}
                    <div className="space-y-4 flex flex-col items-center md:items-start">
                        <h3 className="font-semibold text-foreground">{t('navigation.guides')}</h3>
                        <ul className="space-y-3 flex flex-col items-center md:items-start">
                            <li>
                                <Link
                                    to={links.takeHome}
                                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                                >
                                    {t('navigation.guideTakeHomes')}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to={links.feedback}
                                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                                >
                                    {t('navigation.guideFeedback')}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to={links.ai}
                                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                                >
                                    {t('navigation.guideAiJudgment')}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to={links.analytical}
                                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                                >
                                    {t('navigation.guideAnalyticalTasks')}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* About Section */}
                    <div className="space-y-4 flex flex-col items-center md:items-start">
                        <h3 className="font-semibold text-foreground">{t('navigation.about')}</h3>
                        <ul className="space-y-3 flex flex-col items-center md:items-start">
                            <li>
                                <Link
                                    to={links.about}
                                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                                >
                                    {t('navigation.aboutUs')}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to={links.faq}
                                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                                >
                                    {t('navigation.faq')}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to={links.employers}
                                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                                >
                                    {t('employers.eyebrow')}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to={links.education}
                                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                                >
                                    {t('education.eyebrow')}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Connect Section */}
                    <div className="space-y-4 flex flex-col items-center md:items-start">
                        <h3 className="font-semibold text-foreground">{t('navigation.connect')}</h3>
                        <div className="flex gap-4 justify-center md:justify-start">
                            {/* Social Media Icons - Using simple SVG icons */}
                            <a
                                href={ExternalURLs.DiscordInvite}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-muted-foreground hover:text-primary transition-colors"
                                aria-label="Discord"
                            >
                                <DiscordIcon size={18} />
                            </a>
                            <a
                                href={ExternalURLs.LinkedInPage}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-muted-foreground hover:text-primary transition-colors"
                                aria-label="LinkedIn"
                            >
                                <LinkedInIcon size={18} />
                            </a>
                        </div>
                        <div className="flex gap-1 justify-center md:justify-start">
                            <span className="text-sm text-muted-foreground">{t('common.foundABug')}</span>
                            <button
                                type="button"
                                onClick={() => openVoice({ source: 'footer', presetTopic: 'bug' })}
                                className="text-sm text-muted-foreground underline hover:text-primary transition-colors"
                            >
                                {t('common.letUsKnow')}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="mt-12 pt-8 border-t border-border">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-sm text-muted-foreground text-center md:text-start">
                            © {currentYear} {t('common.appName')}. {t('common.allRightsReserved')}.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-6 items-center">
                            <LanguageSwitcher />
                            <div className="flex gap-6 justify-center">
                                <Link to={links.privacy} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                                    {t('common.privacyPolicy')}
                                </Link>
                                <Link to={links.terms} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                                    {t('common.termsOfService')}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    )
}

